<template>
  <div v-if="hasActions">
    <b-dropdown right split :text="actionName" :class="dropdownClass" @split-click="dropdownClick">
      <DropdownItem
        v-for="actionItem of otherActions"
        :key="actionItem.handle"
        :action="actionItem"
        :assignment="assignment"
      />
    </b-dropdown>
  </div>
</template>

<script lang="ts">
import type { Action } from '@/types/Components/Actions'
import type { AccountType } from '@/types/Models/Account'
import type { Assignment, StatusType } from '@/types/Models/Assignment'
import { BDropdown } from 'bootstrap-vue-next'
import { defineComponent, computed, type PropType, type ComputedRef } from 'vue'
import { useRouter } from 'vue-router'
import {
  ActionsConfig,
  filterAction,
  prepareAction,
  sortAction,
  transformAction,
  useActionHandlers,
} from '@/composables/Assignment/useActionHandlers'
import { ActionColor } from '@/services/Status/ActionColor'
import { useAccountStore } from '@/store/account'
import DropdownItem from '@/views/Components/Assignment/Cells/DropdownItem.vue'

export default defineComponent({
  name: 'ActionsCell',
  components: { BDropdown, DropdownItem },
  props: {
    assignment: {
      type: Object as PropType<Assignment>,
      required: true,
    },
  },
  setup(props) {
    const accountStore = useAccountStore()
    const router = useRouter()

    const assignment: ComputedRef<Assignment> = computed(() => props.assignment)
    const isStaff: ComputedRef<boolean> = computed(() => accountStore.isStaff)
    const accountType = computed<AccountType | null>(() => accountStore.accountType as AccountType | null)

    const actions: ComputedRef<Action[]> = computed(() =>
      ActionsConfig.map((action: Action) => prepareAction(assignment, isStaff.value)(action))
        .filter(filterAction(assignment))
        .map(transformAction(assignment, isStaff.value))
        .sort(sortAction),
    )

    const action: ComputedRef<Action> = computed(() => actions.value[0] || ({} as Action))
    const { actionRoute, actionLink, actionFn, labelsDownloaded } = useActionHandlers(assignment, action)

    const actionColor: ComputedRef<string> = computed(() =>
      labelsDownloaded.value || !accountType.value || !assignment.value.status
        ? 'button-lettuce-green'
        : ActionColor[accountType.value][assignment.value.status as StatusType] || 'button-lettuce-green',
    )

    const hasActions: ComputedRef<boolean> = computed(() => actions.value.length > 0)
    const otherActions: ComputedRef<Action[]> = computed(() => actions.value.slice(1))
    const dropdownClass: ComputedRef<string[]> = computed(() => ['assignment-cta', actionColor.value])

    function dropdownClick() {
      if (actionRoute.value) {
        router.push(actionRoute.value)
      } else if (actionLink.value) {
        window.location.href = actionLink.value
      } else {
        actionFn()
      }
    }

    const actionName: ComputedRef<string> = computed(() =>
      action.value?.name
        ? typeof action.value.name === 'string'
          ? action.value.name
          : action.value.name(assignment.value, isStaff.value) || ''
        : '',
    )

    return {
      actionName,
      hasActions,
      otherActions,
      dropdownClass,
      dropdownClick,
    }
  },
})
</script>
